import * as React from 'react';
import { clsx } from 'clsx';
import { SectionIcon, SectionIconVariant } from '../SectionIcon';

export interface SectionHeadingButton {
  /**
   * The label for the button
   */
  label: string;
  /**
   * Optional icon to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Click handler for the button
   */
  onClick?: () => void;
}

export interface SectionHeadingButtonGroupItem {
  /**
   * The label for the button
   */
  label: string;
  /**
   * Optional icon to display before the label
   */
  icon?: React.ReactNode;
  /**
   * Optional value identifier
   */
  value?: string;
}

export interface SectionHeadingProps {
  /**
   * The heading title text
   */
  title: string;
  /**
   * Show an icon badge next to the title.
   * Can be a boolean (defaults to 'info') or a specific variant.
   * Available variants: 'warning', 'danger', 'info', 'success'
   */
  icon?: boolean | SectionIconVariant;
  /**
   * Avatar configuration - can be an image URL or initials
   */
  avatar?: {
    src?: string;
    initials?: string;
    alt?: string;
  };
  /**
   * Primary button configuration (mutually exclusive with buttonGroup)
   */
  button?: SectionHeadingButton;
  /**
   * Button group configuration (mutually exclusive with button)
   */
  buttonGroup?: {
    items: SectionHeadingButtonGroupItem[];
    activeIndex?: number;
    onChange?: (index: number) => void;
  };
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Placeholder icon for avatar
const AvatarPlaceholder = () => (
  <svg width="22" height="33" viewBox="0 0 22 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="7" r="7" fill="#d1d1d1" />
    <ellipse cx="11" cy="33" rx="11" ry="11" fill="#d1d1d1" />
  </svg>
);

// Arbor Design System section heading styles
const sectionHeadingStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    borderBottom: '1px solid #f8f8f8',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: '56px',
    boxSizing: 'border-box' as const,
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.25',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    margin: 0,
  },
  titleWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  iconBadge: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    border: '1px solid #efefef',
    backgroundColor: '#f8f8f8',
    flexShrink: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  avatarInitials: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#595959',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    height: '32px',
    padding: '0 16px',
    backgroundColor: '#0e8a0e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '99px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  buttonHover: {
    backgroundColor: '#005700',
  },
  buttonGroup: {
    container: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      backgroundColor: '#ffffff',
      border: '1px solid #efefef',
      borderRadius: '99px',
      flexShrink: 0,
    },
    button: {
      base: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '8px 16px',
        borderRadius: '99px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        lineHeight: '1.5',
        transition: 'all 0.15s ease-in-out',
        outline: 'none',
        whiteSpace: 'nowrap' as const,
      },
      default: {
        backgroundColor: 'transparent',
        color: '#595959',
      },
      hover: {
        backgroundColor: '#f8f8f8',
        color: '#2f2f2f',
      },
      active: {
        backgroundColor: '#0e8a0e',
        color: '#ffffff',
      },
      activeHover: {
        backgroundColor: '#005700',
        color: '#ffffff',
      },
    },
  },
};

/**
 * SectionHeading component - Arbor Design System
 *
 * A flexible section heading component that can include an icon, avatar,
 * button, or button group. Button and button group are mutually exclusive.
 *
 * @example
 * ```tsx
 * // Basic heading
 * <SectionHeading title="Section Title" />
 *
 * // With icon
 * <SectionHeading title="Section Title" icon />
 *
 * // With avatar
 * <SectionHeading
 *   title="User Profile"
 *   avatar={{ src: "/avatar.jpg", alt: "User" }}
 * />
 *
 * // With button
 * <SectionHeading
 *   title="Section Title"
 *   button={{ label: "Add Item", onClick: () => {} }}
 * />
 *
 * // With button group
 * <SectionHeading
 *   title="Section Title"
 *   buttonGroup={{
 *     items: [{ label: "Day" }, { label: "Week" }],
 *     activeIndex: 0,
 *     onChange: (index) => {}
 *   }}
 * />
 * ```
 */
export const SectionHeading = React.forwardRef<HTMLDivElement, SectionHeadingProps>(
  ({ title, icon, avatar, button, buttonGroup, className, style, ...props }, ref) => {
    const [buttonHovered, setButtonHovered] = React.useState(false);
    const [hoveredGroupIndex, setHoveredGroupIndex] = React.useState<number | null>(null);

    // Warn if both button and buttonGroup are provided
    if (button && buttonGroup) {
      console.warn('SectionHeading: button and buttonGroup are mutually exclusive. Only button will be rendered.');
    }

    const renderAvatar = () => {
      if (!avatar) return null;

      return (
        <div style={sectionHeadingStyles.avatar}>
          {avatar.src ? (
            <img
              src={avatar.src}
              alt={avatar.alt || 'Avatar'}
              style={sectionHeadingStyles.avatarImage}
            />
          ) : avatar.initials ? (
            <span style={sectionHeadingStyles.avatarInitials}>{avatar.initials}</span>
          ) : (
            <AvatarPlaceholder />
          )}
        </div>
      );
    };

    const renderTitle = () => {
      if (icon) {
        // Determine the icon variant - default to 'info' if boolean true
        const iconVariant: SectionIconVariant = typeof icon === 'string' ? icon : 'info';

        return (
          <div style={sectionHeadingStyles.titleWithIcon}>
            <h2 style={{ ...sectionHeadingStyles.title, flex: 'none' }}>{title}</h2>
            <div style={sectionHeadingStyles.iconBadge}>
              <SectionIcon variant={iconVariant} />
            </div>
          </div>
        );
      }

      return <h2 style={sectionHeadingStyles.title}>{title}</h2>;
    };

    const renderButton = () => {
      if (!button) return null;

      return (
        <button
          type="button"
          style={{
            ...sectionHeadingStyles.button,
            ...(buttonHovered && sectionHeadingStyles.buttonHover),
          }}
          onClick={button.onClick}
          onMouseEnter={() => setButtonHovered(true)}
          onMouseLeave={() => setButtonHovered(false)}
        >
          {button.icon && (
            <span style={{ display: 'flex', alignItems: 'center', width: '16px', height: '16px' }}>
              {button.icon}
            </span>
          )}
          {button.label}
        </button>
      );
    };

    const renderButtonGroup = () => {
      if (!buttonGroup || button) return null;

      const { items, activeIndex = 0, onChange } = buttonGroup;

      return (
        <div style={sectionHeadingStyles.buttonGroup.container}>
          {items.slice(0, 4).map((item, index) => {
            const isActive = index === activeIndex;
            const isHovered = hoveredGroupIndex === index;

            let btnStyle: React.CSSProperties = {
              ...sectionHeadingStyles.buttonGroup.button.base,
            };

            if (isActive) {
              btnStyle = {
                ...btnStyle,
                ...sectionHeadingStyles.buttonGroup.button.active,
                ...(isHovered && sectionHeadingStyles.buttonGroup.button.activeHover),
              };
            } else {
              btnStyle = {
                ...btnStyle,
                ...sectionHeadingStyles.buttonGroup.button.default,
                ...(isHovered && sectionHeadingStyles.buttonGroup.button.hover),
              };
            }

            return (
              <button
                key={item.value || index}
                type="button"
                style={btnStyle}
                onClick={() => {
                  if (index !== activeIndex && onChange) {
                    onChange(index);
                  }
                }}
                onMouseEnter={() => setHoveredGroupIndex(index)}
                onMouseLeave={() => setHoveredGroupIndex(null)}
              >
                {item.icon && (
                  <span style={{ display: 'flex', alignItems: 'center', width: '16px', height: '16px' }}>
                    {item.icon}
                  </span>
                )}
                {item.label}
              </button>
            );
          })}
        </div>
      );
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-section-heading', className)}
        style={{ ...sectionHeadingStyles.container, ...style }}
        {...props}
      >
        {renderAvatar()}
        {renderTitle()}
        {renderButton()}
        {renderButtonGroup()}
      </div>
    );
  }
);

SectionHeading.displayName = 'SectionHeading';
